import { Container } from "@amzn/awsui-components-console";
import React, { ReactNode, useEffect, useRef } from "react";

import LoadingSpinner from "../LoadingSpinner";
import ChatMessages from "./ChatMessages";
import { ChatMessage } from "./types";

export interface ChatContainerProps {
  messages: ChatMessage[];
  loading?: boolean;
  promptInput?: ReactNode;
  /**
   * Identity of the conversation currently rendered. When it changes (a chat
   * is opened, switched, or the page is refreshed into a thread) the view
   * resets to the TOP so the user reads a conversation from its first message.
   * When it stays the same and only `messages` grows (a reply streams into the
   * active chat) the view follows to the BOTTOM. Left undefined, the component
   * always follows to the bottom, preserving the pre-existing behavior.
   */
  conversationId?: string;
}

const ChatContainer: React.FC<ChatContainerProps> = ({
  messages,
  loading,
  promptInput,
  conversationId,
}) => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  // Tracks the conversation the last scroll effect ran for, so we can tell an
  // "opened / switched a chat" render apart from a "new message in the same
  // chat" render. Keyed on identity, not on the messages array's position.
  const prevConversationIdRef = useRef<string | undefined>(undefined);
  // Tracks how many messages the last effect saw. We only follow to the bottom
  // when the count actually GREW within the same conversation — never merely
  // because the effect re-ran. This keeps a re-entry into an existing chat
  // (e.g. returning from the cart, or React.StrictMode's second mount-effect
  // pass) at the top instead of jumping to the bottom.
  const prevMessageCountRef = useRef<number>(0);

  useEffect(() => {
    const conversationChanged =
      prevConversationIdRef.current !== conversationId;
    const messageAppended =
      !conversationChanged && messages.length > prevMessageCountRef.current;
    prevConversationIdRef.current = conversationId;
    prevMessageCountRef.current = messages.length;

    // Defer to the next tick so the (re-rendered) message list has laid out
    // and scrollHeight reflects the current content.
    const timer = setTimeout(() => {
      const node = messagesContainerRef.current;
      if (!node) {
        return;
      }
      if (messageAppended) {
        // A new message arrived in the active chat: follow the latest.
        node.scrollTop = node.scrollHeight;
      } else if (conversationChanged) {
        // Opened / switched into / refreshed / re-entered a chat: show the
        // first messages.
        node.scrollTop = 0;
      }
      // Otherwise (same conversation, no new message) leave the scroll
      // position untouched.
    }, 0);
    return () => clearTimeout(timer);
  }, [messages, conversationId]);

  return (
    <div style={{ height: "100%" }}>
      <Container fitHeight footer={promptInput}>
        {loading ? (
          <LoadingSpinner height="65vh" />
        ) : (
          <div
            ref={messagesContainerRef}
            style={{
              height: "65vh",
              overflowY: "auto",
            }}
          >
            <ChatMessages messages={messages} />
          </div>
        )}
      </Container>
    </div>
  );
};

export default ChatContainer;
